import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getDateOptions, objectMapToSelectOptions } from 'core/utils';
import { corporateService } from 'corporate/services';
import { IVerticalMarket } from 'corporate/types';
import { QuoteStatus, QuoteStatusLabel } from 'quotes/enums';

import { IUpdateQuotesForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IUpdateQuotesForm>) => void;
  onSubmit: (reset?: UseFormReset<IUpdateQuotesForm>) => (data: IUpdateQuotesForm) => void;
  availableStatuses: QuoteStatus[];
}

export const UpdateQuotesFormFields = ({
  isOpen,
  onClose,
  onSubmit,
  availableStatuses,
}: IProps) => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const statusValue = watch('status');
  const validSinceValue = watch('validSince');
  const validUntilValue = watch('validUntil');

  return (
    <Form
      title={t('titleUpdateSelected')}
      isOpen={isOpen}
      onSubmit={onSubmit}
      onClose={onClose}
      cancelBtnLabel={t('buttonLabelCancel')}
    >
      <FormField
        name="closeConfidence"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelCloseConfidence'),
          slotProps: {
            input: {
              min: 0,
              max: 100,
              allowedDecimalSeparators: [],
              decimalScale: 0,
              suffix: '%',
            },
          },
        }}
      />

      <FormField
        name="status"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelStatus'),
          options: objectMapToSelectOptions(
            Object.fromEntries(
              availableStatuses.map((status) => [status, QuoteStatusLabel[status]])
            )
          ),
        }}
      />

      {QuoteStatus.LOST === statusValue && (
        <FormField
          name="quoteStatusLostNote"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelReasonQuoteLose'),
          }}
        />
      )}

      <FormField
        name="closeDate"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelCloseDate'),
          options: getDateOptions(),
        }}
      />

      <FormField
        name="verticalMarket"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelVerticalMarket'),
          getOptions: corporateService.getVerticalMarketsAutocomplete,
          getLabel: (option: IVerticalMarket) => option?.name,
          enabledOnFocus: false,
        }}
      />

      <FormGroupWrapper name="validDates">
        <FormField
          name="validSince"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelValidSince'),
            maxDate: validUntilValue,
          }}
        />
        <FormField
          name="validUntil"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelValidUntil'),
            minDate: validSinceValue,
          }}
        />
      </FormGroupWrapper>
    </Form>
  );
};
